import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { exerciseOptions, fetchData, youtubeOptions } from '../utils/fetchData';
import Loader from '../components/Loader';

const ExerciseDetail = () => {
  const [exerciseDetail, setExerciseDetail] = useState(null);
  const [exerciseVideos, setExerciseVideos] = useState([]);
  const { id } = useParams();

  useEffect(() => {
    const fetchExerciseData = async () => {
      try {
        const exerciseDbUrl = 'https://exercisedb.p.rapidapi.com';
        const youtubeSearchUrl = 'https://youtube-search-and-download.p.rapidapi.com';

        const exerciseDetailData = await fetchData(
          `${exerciseDbUrl}/exercises/exercise/${id}`,
          exerciseOptions
        );

        setExerciseDetail(exerciseDetailData);

        const youtubeSearchData = await fetchData(
          `${youtubeSearchUrl}/search?query=${exerciseDetailData.name} exercise`,
          youtubeOptions
        );

        setExerciseVideos(youtubeSearchData.contents ?? []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchExerciseData();
  }, [id]);

  if (!exerciseDetail) return <Loader />;

  const { bodyPart, gifUrl, name, target, equipment } = exerciseDetail;

  return (
    <Box sx={{ mt: { lg: '96px', xs: '60px' } }}>
      {/* Top Section */}
      <Stack
        direction={{ lg: 'row', xs: 'column' }}
        gap="60px"
        p="20px"
        alignItems="center"
      >
        <img
          src={gifUrl}
          alt={name}
          loading="lazy"
          className="detail-image"
          style={{ borderRadius: '20px', width: '400px' }}
        />

        <Stack sx={{ gap: { lg: '35px', xs: '20px' } }}>
          <Typography variant="h3" fontWeight="bold" textTransform="capitalize">
            {name}
          </Typography>

          <Typography variant="h6" color="text.secondary">
            Exercises keep you strong.{' '}
            <strong style={{ textTransform: 'capitalize' }}>{name}</strong> is one
            of the best exercises to target your <strong>{target}</strong>. It
            will help you improve your mood and gain energy.
          </Typography>

          {[
            { label: 'Body Part', value: bodyPart },
            { label: 'Target Muscle', value: target },
            { label: 'Equipment', value: equipment },
          ].map(({ label, value }) => (
            <Stack key={label} direction="row" alignItems="center" gap="24px">
              <Box
                sx={{
                  background: '#FFF2DB',
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span style={{ fontSize: '32px' }}>
                  {label === 'Body Part'
                    ? '🏋️'
                    : label === 'Target Muscle'
                    ? '💪'
                    : '🔧'}
                </span>
              </Box>

              <Typography
                textTransform="capitalize"
                fontSize="22px"
                fontWeight="bold"
              >
                {value}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>

      {/* YouTube Videos Section */}
      <Box sx={{ mt: { lg: '109px', xs: '50px' } }} p="20px">
        <Typography variant="h4" fontWeight="bold" mb="33px">
          Watch{' '}
          <span style={{ color: '#FF2625', textTransform: 'capitalize' }}>
            {name}
          </span>{' '}
          Exercise Videos
        </Typography>

        <Stack
          direction="row"
          flexWrap="wrap"
          justifyContent="flex-start"
          gap="40px"
        >
          {exerciseVideos?.slice(0, 6).map((item) =>
            item.video ? (
              <a
                key={item.video.videoId}
                className="exercise-video"
                href={`https://www.youtube.com/watch?v=${item.video.videoId}`}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: 'none', color: '#000' }}
              >
                <img
                  src={item.video?.thumbnails?.[0]?.url}
                  alt={item.video?.title}
                  style={{
                    borderTopLeftRadius: '20px',
                    width: '350px',
                  }}
                />

                <Box>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    mt="10px"
                    width="350px"
                  >
                    {item.video?.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mt="5px"
                  >
                    {item.video?.channelName}
                  </Typography>
                </Box>
              </a>
            ) : null
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default ExerciseDetail;